import type { TableRowDefinition } from "$components/table";
import { parseISO } from "date-fns";
import { format, utcToZonedTime } from "date-fns-tz";
import type { IntlShape } from "react-intl";
import {
  NigeriaDailyOutpatientAttendance,
  NigeriaDailyOutpatientHepatitisResult,
  NigeriaDailyOutpatientMalariaDiagnosis,
  NigeriaDailyOutpatientOutcome,
  NigeriaDailyOutpatientPreReferralTreatment,
  type NigeriaDailyOutpatientRegisterRecord,
} from "../../../../../__generated__/base.gql";
import { CellWithSubscript } from "../../../_lib/cell-with-subscript";
import { getAge } from "../../../_lib/get-age";

const yesNo = (value?: boolean | null): "Yes" | "No" | "-" => {
  if (typeof value !== "boolean") {
    return "-";
  }

  if (value) return "Yes";

  return "No";
};

const attendanceType = (
  attendance?: NigeriaDailyOutpatientAttendance | null,
) => {
  switch (attendance) {
    case NigeriaDailyOutpatientAttendance.FollowUp:
      return "Follow up";
    case NigeriaDailyOutpatientAttendance.New:
      return "New";
    default:
      return "-";
  }
};

const outcome = (o?: NigeriaDailyOutpatientOutcome | null) => {
  switch (o) {
    case NigeriaDailyOutpatientOutcome.NotTreated:
      return "Not treated";
    case NigeriaDailyOutpatientOutcome.Treated:
      return "Treated";
    case NigeriaDailyOutpatientOutcome.Referred:
      return "Referred";
    default:
      return "-";
  }
};

const malariaDiagnosis = (
  d?: NigeriaDailyOutpatientMalariaDiagnosis | null,
) => {
  switch (d) {
    case NigeriaDailyOutpatientMalariaDiagnosis.ClinicalDiagnosisOnly:
      return "Clinical diagnosis only";
    case NigeriaDailyOutpatientMalariaDiagnosis.MicroscopyNeg:
      return "Microscopy, negative";
    case NigeriaDailyOutpatientMalariaDiagnosis.MicroscopyPos:
      return "Microscopy, positive";
    case NigeriaDailyOutpatientMalariaDiagnosis.RdtNeg:
      return "RDT, negative";
    case NigeriaDailyOutpatientMalariaDiagnosis.RdtPos:
      return "RDT, positive";
    default:
      return "-";
  }
};

const preRef = (pf?: NigeriaDailyOutpatientPreReferralTreatment | null) => {
  switch (pf) {
    case NigeriaDailyOutpatientPreReferralTreatment.ImIvArtesunate:
      return "IM/IV Artesunate";
    case NigeriaDailyOutpatientPreReferralTreatment.IvArtesunate:
      return "IV Artesunate";
    case NigeriaDailyOutpatientPreReferralTreatment.No:
      return "No";
    case NigeriaDailyOutpatientPreReferralTreatment.Other:
      return "Other";
    default:
      return "-";
  }
};

const hepatitis = (hep?: NigeriaDailyOutpatientHepatitisResult | null) => {
  switch (hep) {
    case NigeriaDailyOutpatientHepatitisResult.NotTested:
      return "Not tested";
    case NigeriaDailyOutpatientHepatitisResult.TestedNegative:
      return "Tested negative";
    case NigeriaDailyOutpatientHepatitisResult.TestedPositiveReferred:
      return "Tested positive, referred";
    case NigeriaDailyOutpatientHepatitisResult.TestedPositiveTreated:
      return "Tested negative, treated";
    default:
      return "-";
  }
};

const weight = (w?: number | null) => (w ? `${w}kg` : "-");
const height = (h?: number | null) => (h ? `${h}cm` : "-");
const titleCase = (s: string) =>
  s.charAt(0).toUpperCase() + s.substring(1).toLowerCase();

export function createRowFormatter(
  timeZone: string,
  formatMessage: IntlShape["formatMessage"],
) {
  return (record: NigeriaDailyOutpatientRegisterRecord): TableRowDefinition => {
    const date = utcToZonedTime(parseISO(record.date), timeZone);

    const tbClinicalScreeningFormValue = record.tbClinicalScreeningForm
      ? "1"
      : "0";
    const tbClinicalScreeningForm =
      typeof record.tbClinicalScreeningForm === "boolean"
        ? tbClinicalScreeningFormValue
        : "-";
    return {
      id: record.id,
      patientUuid: record.patient.uuid,
      date: CellWithSubscript({
        script: format(date, "dd MMM", { timeZone }),
        subscript: format(date, "yyyy", { timeZone }),
      }),
      name: record.patient.person.name.fullname,
      cardNumber: record.patient.cardId ?? record.patient.passport?.uid ?? "-",
      gender: record.patient.person.gender,
      age: getAge(
        { date, dateOfBirth: record.patient.person.dob },
        formatMessage,
      ),
      isPregnant: yesNo(record.isPregnant),
      attendanceType: attendanceType(record.attendanceType),
      weight: weight(record.weight),
      height: height(record.height),
      BMI: record.BMI?.toString() ?? "-",
      presentingComplaint: record.presentingComplaint
        ? record.presentingComplaint
        : "-",
      investigationsCompleted: record.investigationsCompleted?.length
        ? record.investigationsCompleted?.join(", ")
        : "-",
      diagnosis: record.diagnosis?.length ? record.diagnosis?.join(", ") : "-",
      drugsGiven: record.drugsGiven?.length
        ? record.drugsGiven?.join(", ")
        : "-",
      outcome: outcome(record.outcomeOfVisit),
      malariaDiagnosis: malariaDiagnosis(record.malariaDiagnosis),
      actGiven: yesNo(record.actGiven),
      otherAntimalarialGiven: yesNo(record.otherAntimalarialGiven),
      severeMalaria: yesNo(record.severeMalaria),
      preReferralTreatment: preRef(record.preReferralTreatment),
      tbClinicallyScreened: yesNo(record.tbClinicallyScreened),
      tbClinicalScreeningForm,
      tbReferred: yesNo(record.tbReferred),
      hepatitisB: hepatitis(record.hepatitisB),
      hepatitisC: hepatitis(record.hepatitisC),
      gbvCaseSeen:
        record.gbvCaseSeen?.map((g) => titleCase(g)).join(", ") ?? "-",
      postGBVCareReceived: yesNo(record.postGBVCareReceived),
      gbvCaseReferred: yesNo(record.gbvCaseReferred),
    };
  };
}
